import React, { useEffect, useState } from 'react';
import { api } from '../services/api';

const formatDateTime = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return `${date.toLocaleDateString()} ${date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`;
};

const formatEventType = (value) => {
  if (value === 'self_leave') return 'Self Leave';
  if (value === 'manager_remove') return 'Manager Remove';
  if (value === 'org_close') return 'Org Close';
  return value || '—';
};

const statusBadgeClass = (status) => {
  if (status === 'succeeded') return 'bg-success';
  if (status === 'skipped') return 'bg-secondary';
  if (status === 'failed') return 'bg-danger';
  return 'bg-warning text-dark';
};

const OpsSupportTicketCleanup = () => {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [includeResolved, setIncludeResolved] = useState(false);
  const [error, setError] = useState('');
  const [status, setStatus] = useState({ type: '', message: '' });
  const [retryingTaskId, setRetryingTaskId] = useState(null);

  const loadTasks = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await api.get('/acct-mgmt/v1/support/identity-cleanup-tasks', {
        params: { include_resolved: includeResolved },
      });
      setTasks(response.data?.tasks || []);
      window.dispatchEvent(new Event('finbase:support-identity-cleanup-count-refresh'));
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load support portal identity cleanup tasks.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTasks();
  }, [includeResolved]);

  const retryTask = async (taskId) => {
    setRetryingTaskId(taskId);
    setError('');
    setStatus({ type: '', message: '' });
    try {
      const response = await api.post(`/acct-mgmt/v1/support/identity-cleanup-tasks/${taskId}/retry`);
      const nextStatus = response.data?.task?.status;
      setStatus({
        type: nextStatus === 'failed' ? 'warning' : 'success',
        message: nextStatus === 'failed'
          ? 'Cleanup retry completed but still requires follow-up.'
          : 'Cleanup retry completed.',
      });
      await loadTasks();
      window.dispatchEvent(new Event('finbase:support-identity-cleanup-count-refresh'));
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to retry support portal identity cleanup.');
    } finally {
      setRetryingTaskId(null);
    }
  };

  return (
    <div>
      <div className="d-flex align-items-center justify-content-between mb-3">
        <div>
          <h2 className="h4 fw-bold mb-1">Support Portal Identity Cleanup</h2>
          <div className="text-muted">Review and retry Zendesk identity orphaning after users leave organizations.</div>
        </div>
        <div className="d-flex align-items-center gap-3">
          <div className="form-check form-switch finbase-toggle m-0">
            <input
              className="form-check-input"
              type="checkbox"
              id="includeResolvedCleanupTasks"
              checked={includeResolved}
              onChange={(event) => setIncludeResolved(event.target.checked)}
            />
            <label className="form-check-label" htmlFor="includeResolvedCleanupTasks">
              Include Resolved
            </label>
          </div>
          <button type="button" className="btn btn-outline-primary btn-sm" onClick={loadTasks} disabled={loading || Boolean(retryingTaskId)}>
            {loading ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>
      </div>

      {status.message && <div className={`alert alert-${status.type}`} role="alert">{status.message}</div>}
      {error && <div className="alert alert-danger" role="alert">{error}</div>}

      <div className="alert alert-info" role="alert">
        Cleanup tasks orphan the org-scoped Zendesk identity for users who leave an organization. Tickets stay attached to the same Zendesk requester, but that requester no longer uses the former user's real email.
      </div>

      <div className="card">
        <div className="card-body">
          {loading ? (
            <div className="text-muted">Loading support portal identity cleanup tasks...</div>
          ) : tasks.length === 0 ? (
            <div className="text-muted">{includeResolved ? 'No support portal identity cleanup tasks found.' : 'No unresolved support portal identity cleanup tasks found.'}</div>
          ) : (
            <div className="table-responsive">
              <table className="table table-sm align-middle mb-0">
                <thead>
                  <tr>
                    <th>Created</th>
                    <th>Organization</th>
                    <th>Former User</th>
                    <th>Event</th>
                    <th>Status</th>
                    <th>Zendesk Identity</th>
                    <th>Orphan Email</th>
                    <th>Attempts</th>
                    <th>Last Error</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {tasks.map((task) => (
                    <tr key={task.id}>
                      <td>{formatDateTime(task.created_at)}</td>
                      <td>
                        <div className="fw-semibold">{task.org_name || task.org_name_snapshot || '—'}</div>
                        <div className="text-muted small">Org #{task.org_id}</div>
                      </td>
                      <td>
                        <div className="fw-semibold">{task.user_email_snapshot || `User #${task.user_id}`}</div>
                        <div className="text-muted small">{task.user_email_snapshot || `User #${task.user_id}`}</div>
                      </td>
                      <td>{formatEventType(task.trigger_event_type)}</td>
                      <td>
                        <span className={`badge ${statusBadgeClass(task.status)}`}>
                          {String(task.status || 'pending').replace(/_/g, ' ')}
                        </span>
                      </td>
                      <td>
                        <div>{task.zendesk_user_id || '—'}</div>
                        <div className="text-muted small">{task.zendesk_user_external_id || '—'}</div>
                      </td>
                      <td>
                        <div>{task.orphan_email || '—'}</div>
                        <div className="text-muted small">{task.orphan_name || '—'}</div>
                      </td>
                      <td>
                        <div>{task.attempt_count || 0}</div>
                        <div className="text-muted small">{formatDateTime(task.last_attempted_at)}</div>
                      </td>
                      <td style={{ minWidth: 220 }}>
                        {task.last_error ? (
                          <span className="text-danger small">{task.last_error}</span>
                        ) : (
                          <span className="text-muted small">—</span>
                        )}
                      </td>
                      <td>
                        {['pending', 'failed'].includes(task.status) ? (
                          <button
                            type="button"
                            className="btn btn-outline-primary btn-sm"
                            onClick={() => retryTask(task.id)}
                            disabled={Boolean(retryingTaskId)}
                          >
                            {retryingTaskId === task.id ? 'Retrying...' : 'Retry'}
                          </button>
                        ) : (
                          <span className="text-muted small">Resolved {formatDateTime(task.resolved_at)}</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default OpsSupportTicketCleanup;
